import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, GitBranch } from 'lucide-react';

const OPS = [
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'gte', label: '>=' },
  { value: 'lte', label: '<=' },
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
];

function RuleRow({ rule, fields, onUpdate, onRemove }) {
  return (
    <div className="flex items-center gap-1.5 group">
      <Select value={rule.metric || ''} onValueChange={v => onUpdate({ metric: v })}>
        <SelectTrigger className="h-7 w-[140px] text-[10px] rounded-md bg-muted/30 border-border/30"><SelectValue placeholder="Metric" /></SelectTrigger>
        <SelectContent>{fields.filter(f => f.semantic_type === 'measure' || f.data_type === 'number' || f.data_type === 'float').map(f => (
          <SelectItem key={f.name} value={f.name} className="text-xs">{f.business_name || f.display_name || f.name}</SelectItem>
        ))}</SelectContent>
      </Select>
      <Select value={rule.operator || 'gt'} onValueChange={v => onUpdate({ operator: v })}>
        <SelectTrigger className="h-7 w-[60px] text-[10px] rounded-md bg-muted/30 border-border/30"><SelectValue /></SelectTrigger>
        <SelectContent>{OPS.map(op => <SelectItem key={op.value} value={op.value} className="text-xs">{op.label}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="number" value={rule.threshold ?? ''} onChange={e => onUpdate({ threshold: e.target.value })} placeholder="Value" className="h-7 w-[80px] text-[10px] rounded-md bg-muted/30 border-border/30 text-foreground" />
      <button onClick={onRemove} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"><Trash2 className="w-3 h-3" /></button>
    </div>
  );
}

/**
 * ConditionBuilder with AND/OR groups (max 2 levels, non-recursive).
 * conditions = { logic: 'AND'|'OR', rules: [ { metric, operator, threshold } | { logic, rules } ] }
 */
export function ConditionBuilder({ conditions, onChange, fields }) {
  const logic = conditions?.logic || 'AND';
  const rules = conditions?.rules || [];

  const updateRule = (idx, updates) => { const n = [...rules]; n[idx] = { ...n[idx], ...updates }; onChange({ logic, rules: n }); };
  const removeRule = (idx) => { onChange({ logic, rules: rules.filter((_, i) => i !== idx) }); };
  const addRule = () => { onChange({ logic, rules: [...rules, { metric: '', operator: 'gt', threshold: '' }] }); };
  const toggleLogic = () => { onChange({ logic: logic === 'AND' ? 'OR' : 'AND', rules }); };
  const addGroup = () => { onChange({ logic, rules: [...rules, { logic: logic === 'AND' ? 'OR' : 'AND', rules: [{ metric: '', operator: 'gt', threshold: '' }] }] }); };

  // Nested group helpers
  const updateNestedRule = (gIdx, rIdx, updates) => {
    const n = [...rules];
    const g = { ...n[gIdx], rules: [...n[gIdx].rules] };
    g.rules[rIdx] = { ...g.rules[rIdx], ...updates };
    n[gIdx] = g;
    onChange({ logic, rules: n });
  };
  const removeNestedRule = (gIdx, rIdx) => {
    const n = [...rules];
    const g = { ...n[gIdx], rules: n[gIdx].rules.filter((_, i) => i !== rIdx) };
    n[gIdx] = g;
    onChange({ logic, rules: n });
  };
  const addNestedRule = (gIdx) => {
    const n = [...rules];
    const g = { ...n[gIdx], rules: [...n[gIdx].rules, { metric: '', operator: 'gt', threshold: '' }] };
    n[gIdx] = g;
    onChange({ logic, rules: n });
  };
  const toggleNestedLogic = (gIdx) => {
    const n = [...rules];
    n[gIdx] = { ...n[gIdx], logic: n[gIdx].logic === 'AND' ? 'OR' : 'AND' };
    onChange({ logic, rules: n });
  };

  return (
    <div className="space-y-2" data-testid="condition-builder">
      {/* Root logic toggle */}
      <div className="flex items-center gap-2 mb-1">
        <button onClick={toggleLogic} className={`text-[9px] font-bold px-2 py-0.5 rounded-md ${logic === 'AND' ? 'bg-blue-500/15 text-blue-400 border border-blue-500/25' : 'bg-orange-500/15 text-orange-400 border border-orange-500/25'}`} data-testid="root-logic-toggle">{logic}</button>
        <span className="text-[9px] text-muted-foreground">{logic === 'AND' ? 'All must be true' : 'Any can be true'}</span>
      </div>

      {rules.map((rule, idx) => (
        <div key={idx}>
          {rule.logic ? (
            /* Nested group — rendered inline (no recursion) */
            <div className="pl-3 border-l-2 border-primary/15 ml-1 space-y-2 rounded-lg border border-border/30 bg-muted/10 p-2" data-testid={`nested-group-${idx}`}>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleNestedLogic(idx)} className={`text-[9px] font-bold px-2 py-0.5 rounded-md ${rule.logic === 'AND' ? 'bg-blue-500/15 text-blue-400 border border-blue-500/25' : 'bg-orange-500/15 text-orange-400 border border-orange-500/25'}`}>{rule.logic}</button>
                <span className="text-[9px] text-muted-foreground">{rule.logic === 'AND' ? 'All must be true' : 'Any can be true'}</span>
                <button onClick={() => removeRule(idx)} className="ml-auto text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
              </div>
              {(rule.rules || []).map((nestedRule, nIdx) => (
                <div key={nIdx} className="flex items-center gap-1.5">
                  <RuleRow rule={nestedRule} fields={fields} onUpdate={u => updateNestedRule(idx, nIdx, u)} onRemove={() => removeNestedRule(idx, nIdx)} />
                  {nIdx < rule.rules.length - 1 && <span className={`text-[8px] font-bold ${rule.logic === 'AND' ? 'text-blue-400' : 'text-orange-400'}`}>{rule.logic}</span>}
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={() => addNestedRule(idx)} className="h-5 text-[9px] gap-1 text-muted-foreground"><Plus className="w-3 h-3" /> Condition</Button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <RuleRow rule={rule} fields={fields} onUpdate={u => updateRule(idx, u)} onRemove={() => removeRule(idx)} />
              {idx < rules.length - 1 && !rules[idx + 1]?.logic && <span className={`text-[8px] font-bold ${logic === 'AND' ? 'text-blue-400' : 'text-orange-400'}`}>{logic}</span>}
            </div>
          )}
        </div>
      ))}

      <div className="flex items-center gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={addRule} className="h-6 text-[9px] gap-1 text-muted-foreground hover:text-foreground" data-testid="add-condition"><Plus className="w-3 h-3" /> Condition</Button>
        <Button variant="ghost" size="sm" onClick={addGroup} className="h-6 text-[9px] gap-1 text-muted-foreground hover:text-foreground" data-testid="add-group"><GitBranch className="w-3 h-3" /> {logic === 'AND' ? 'OR' : 'AND'} Group</Button>
      </div>
    </div>
  );
}

export function conditionSummary(conditions, labelFn) {
  if (!conditions?.rules?.length) return '';
  const lg = conditions.logic || 'AND';
  const lbl = labelFn || ((m) => m);
  return conditions.rules.map(r => {
    if (r.logic) return `(${conditionSummary(r, labelFn)})`;
    const op = OPS.find(o => o.value === r.operator)?.label || r.operator;
    return `${lbl(r.metric)} ${op} ${r.threshold}`;
  }).join(` ${lg} `);
}
